import React, { useEffect, useState } from "react";
import {
  fmCrosspostSiteNameSetting,
  fmCrosspostBaseUrlSetting,
} from "../../lib/instanceSettings";
import { useSingle } from "../../lib/crud/withSingle";
import FormControlLabel from "@/lib/vendor/@material-ui/core/src/FormControlLabel";
import Checkbox from "@/lib/vendor/@material-ui/core/src/Checkbox";
import Button from "@/lib/vendor/@material-ui/core/src/Button";
import LoginIcon from "@/lib/vendor/@material-ui/icons/src/LockOpen"
import UnlinkIcon from "@/lib/vendor/@material-ui/icons/src/RemoveCircle";
import { gql, useMutation } from "@apollo/client";
import { useOnFocusTab } from "../hooks/useOnFocusTab";
import { combineUrls } from "../../lib/vulcan-lib/utils";
import { useCurrentUser } from "../common/withUser";
import { useMessages } from "../common/withMessages";
import {
  crossposterDetailsRoute,
  CrosspostRouteResponse,
  generateTokenRoute,
} from "@/lib/fmCrosspost/routes";
import { TypedFieldApi } from "@/components/tanstack-form-components/BaseAppForm";
import { defineStyles, useStyles } from "../hooks/useStyles";
import { isFriendlyUI } from "@/themes/forumTheme";
import Loading from "../vulcan-core/Loading";

const styles = defineStyles('FMCrosspostControl', (theme: ThemeType) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    margin: 8,
    "& .MuiTypography-root": {
      color: theme.palette.text.normal,
    },
  },
  link: {
    color: theme.palette.primary.main,
  },
  label: {
    fontFamily: isFriendlyUI ? theme.palette.fonts.sansSerifStack : undefined,
  },
  crosspostMessage: {
    marginBottom: 12,
  },
  button: {
    background: theme.palette.buttons.imageUpload.background,
    "&:hover": {
      background: theme.palette.buttons.imageUpload.hoverBackground,
    },
    color: theme.palette.text.invertedBackgroundText,
  },
  buttonIcon: {
    fontSize: 18,
    marginRight: theme.spacing.unit,
  },
  error: {
    color: theme.palette.error.main,
    margin: "8px 0",
  },
}));

type CrossposterDetails = CrosspostRouteResponse<typeof crossposterDetailsRoute>;

/**
 * FMCrosspostAccount displays the user's account on the other platform after
 * it's already been authorized
 */
const FMCrosspostAccount = ({fmCrosspostUserId}: {
  fmCrosspostUserId: string,
}) => {
  const {flash} = useMessages();
  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState<CrossposterDetails | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const result = await crossposterDetailsRoute.makeRequest(
          {userId: fmCrosspostUserId},
          {foreignRequest: true},
        );
        setDocument(result);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch crossposter", e);
        flash(e instanceof Error ? e.message : "Failed to fetch crossposter");
      } finally {
        setLoading(false);
      }
    })();
  }, [flash, fmCrosspostUserId]);

  const classes = useStyles(styles);
  const link = `${fmCrosspostBaseUrlSetting.get()}users/${document?.slug}`;
  if (!document || loading) {
    return <Loading/>
  }
  return <div className={classes.crosspostMessage}>
    This post will be crossposted to {fmCrosspostSiteNameSetting.get()} by
    your account <a className={classes.link} href={link} target="_blank" rel="noreferrer">
      {document.displayName}
    </a>
  </div>
}

/**
 * FMCrosspostAuth shows the user their account if they've already set up crossposting along
 * with an option to remove the account to perhaps add another, or, if they've not set up an
 * account yet they'll be prompted to do so.
 */
const FMCrosspostAuth = ({fmCrosspostUserId, loading, onClickLogin, onClickUnlink}: {
  fmCrosspostUserId?: string,
  loading: boolean,
  onClickLogin: () => void,
  onClickUnlink: () => void,
}) => {
  const classes = useStyles(styles);

  if (loading) {
    return (
      <Loading />
    );
  }

  return fmCrosspostUserId
    ? (
      <div>
        <FMCrosspostAccount fmCrosspostUserId={fmCrosspostUserId} />
        <Button onClick={onClickUnlink} className={classes.button}>
          <UnlinkIcon className={classes.buttonIcon} />
          Unlink this account
        </Button>
      </div>
    )
    :(
      <div>
        <Button onClick={onClickLogin} className={classes.button}>
          <LoginIcon className={classes.buttonIcon} />
          Login to {fmCrosspostSiteNameSetting.get()} to enable crossposting
        </Button>
      </div>
    );
}

/**
 * FMCrosspostControl is the main form component for setting up crossposts
 * It allows the user to choose whether or not to make this post a crosspost,
 * and it also allows them to connect or disconnect their account on the other
 * platform.
 */
export const FMCrosspostControl = ({ field }: {
  field: TypedFieldApi<{ isCrosspost: boolean, hostedHere?: boolean | null, foreignPostId?: string | null }>
}) => {
  const classes = useStyles(styles);
  const currentUser = useCurrentUser();
  const {isCrosspost} = field.state.value ?? {};
  if (!currentUser) throw new Error("FMCrosspostControl should only appear when logged in");

  const [unlink, {loading: loadingUnlink}] = useMutation(gql`
    mutation unlinkCrossposter {
      unlinkCrossposter
    }
  `, {errorPolicy: "all"});
  const {document, refetch, loading: loadingDocument} = useSingle({
    documentId: currentUser._id,
    collectionName: "Users",
    fragmentName: "UsersCrosspostInfo",
    notifyOnNetworkStatusChange: true,
  });
  const [error, setError] = useState<string | null>(null);

  const loading = loadingUnlink || loadingDocument;

  useOnFocusTab(() => {
    if (!loading && !document?.fmCrosspostUserId) {
      refetch();
    }
  });

  const onClickLogin = async () => {
    try {
      const {token} = await generateTokenRoute.makeRequest({});
      if (token) {
        const url = combineUrls(fmCrosspostBaseUrlSetting.get() ?? "", `crosspostLogin?token=${token}`);
        window.open(url, "_blank")?.focus();
      } else {
        setError("Couldn't create login token");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create login token");
    }
  }

  const onClickUnlink = async () => {
    await unlink();
    await refetch();
  }

  return (
    <div className={classes.root}>
      <FormControlLabel
        label={
          <div className={classes.label}>
            Crosspost to {fmCrosspostSiteNameSetting.get()}
          </div>
        }
        control={
          <Checkbox
            checked={isCrosspost}
            onChange={(_, checked) => {
              field.handleChange({
                isCrosspost: checked,
                hostedHere: true,
              })
            }}
            disableRipple
          />
        }
      />
      {error && <div className={classes.error}>Error: {error}</div>}
      {!error && isCrosspost &&
        <FMCrosspostAuth
          fmCrosspostUserId={document?.fmCrosspostUserId ?? undefined}
          loading={loading}
          onClickLogin={onClickLogin}
          onClickUnlink={onClickUnlink}
        />
      }
    </div>
  );
};
